import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { getStudentClassSchedule, getAlternatePlans, getBlockSchedules } from "../../lib/api";
import { printPage } from "../../lib/print";
import { planActiveOnDate, weekdayKeyOf } from "../../lib/altPlans";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
const DAY_LABEL = { monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday" };

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const fmtLong = (isoStr) => new Date(isoStr + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const CAL_STYLES = `
  .cal-table { width:100%; border-collapse:collapse; margin-top:12px; }
  .cal-table th, .cal-table td { border:1px solid #cbd5e1; padding:6px 8px; vertical-align:top; font-size:11px; }
  .cal-table th { background:#00305E; color:#fff; font-size:11px; }
  .cal-blockcol { background:#f1f5f9; font-weight:600; width:90px; }
  .cal-daysub { font-size:10px; color:#475569; font-weight:400; }
  .cal-off { background:#fef3c7; color:#92400e; border-radius:4px; padding:2px 4px; display:inline-block; margin-top:3px; font-size:10px; font-weight:600; }
  .cal-noexp { color:#94a3b8; font-style:italic; }
  .cal-subject { font-weight:600; color:#0f172a; }
  .cal-meta { color:#64748b; }
  .mon-grid { width:100%; border-collapse:collapse; margin-top:12px; }
  .mon-grid th { background:#00305E; color:#fff; padding:6px; font-size:11px; border:1px solid #cbd5e1; }
  .mon-grid td { border:1px solid #cbd5e1; height:74px; width:20%; vertical-align:top; padding:4px 5px; font-size:10px; }
  .mon-date { font-weight:700; color:#0f172a; font-size:11px; }
  .mon-other { background:#f8fafc; color:#cbd5e1; }
  .mon-times { color:#334155; margin-top:2px; }
  .mon-off { color:#92400e; background:#fef3c7; border-radius:3px; padding:1px 3px; margin-top:2px; display:inline-block; font-weight:600; }
  .legend { margin-top:10px; font-size:11px; color:#475569; }
`;

export default function StudentScheduleCalendarPrint({ student, studentId }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("weekly");
  const [refDate, setRefDate] = useState(iso(new Date()));
  const [busy, setBusy] = useState(false);

  const studentName = `${student?.first_name || ""} ${student?.last_name || ""}`.trim() || "Student";

  const loadData = async () => {
    const [csRes, apRes, bsRes] = await Promise.allSettled([
      getStudentClassSchedule(studentId),
      getAlternatePlans({ student_id: studentId }),
      getBlockSchedules(),
    ]);
    const classSchedule = (csRes.status === "fulfilled" && csRes.value.data?.class_schedule) || {};
    const plans = (apRes.status === "fulfilled" && apRes.value.data) || [];
    const activePlans = plans.filter((p) => p.active !== false);
    const fullBlocks = (bsRes.status === "fulfilled" && bsRes.value.data?.full_day?.blocks) || [];
    const blockTime = {};
    fullBlocks.forEach((b) => { blockTime[b.name.toLowerCase().replace(/\s/g, "")] = `${b.start_time}-${b.end_time}`; });
    return { classSchedule, activePlans, blockTime };
  };

  const dayTimes = (dayKey) => {
    const s = (student?.schedule || {})[dayKey];
    if (s && s.expected === false) return { expected: false };
    if (s && (s.start || s.end)) return { expected: true, label: `${s.start || "08:30"}–${s.end || ""}` };
    return { expected: true, label: "" };
  };

  const offsiteFor = (activePlans, dateIso) => {
    const hit = activePlans.filter((p) => planActiveOnDate(p, dateIso));
    if (!hit.length) return "";
    return hit.map((p) => p.destination).join(", ");
  };

  const printWeekly = async ({ classSchedule, activePlans, blockTime }) => {
    const mon = mondayOf(new Date(refDate + "T00:00:00"));
    const dates = WEEKDAYS.map((_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return iso(d); });
    const rangeLabel = `${fmtLong(dates[0])} – ${fmtLong(dates[4])}`;

    const allBlocks = new Set();
    WEEKDAYS.forEach((d) => (classSchedule[d] || []).forEach((c) => allBlocks.add(c.block)));
    const blocks = [...allBlocks].sort();

    let head = `<tr><th class="cal-blockcol">Block</th>`;
    WEEKDAYS.forEach((d, i) => {
      const t = dayTimes(d);
      const off = offsiteFor(activePlans, dates[i]);
      head += `<th>${DAY_LABEL[d]}<div class="cal-daysub">${esc(fmtLong(dates[i]))}</div>` +
        `<div class="cal-daysub">${t.expected ? esc(t.label || "") : "Not expected"}</div>` +
        (off ? `<div class="cal-off">Off-site: ${esc(off)}</div>` : "") + `</th>`;
    });
    head += `</tr>`;

    let rows = "";
    if (blocks.length === 0) {
      rows = `<tr><td colspan="6" style="text-align:center;color:#64748b;padding:16px;">No class schedule found in the current Master Schedule.</td></tr>`;
    } else {
      blocks.forEach((blk) => {
        rows += `<tr><td class="cal-blockcol">${esc(blk.replace("block", "Block "))}<div class="cal-daysub">${esc(blockTime[blk] || "")}</div></td>`;
        WEEKDAYS.forEach((d) => {
          const cls = (classSchedule[d] || []).find((c) => c.block === blk);
          if (cls) {
            rows += `<td><div class="cal-subject">${esc(cls.subject)}</div>` +
              (cls.lead ? `<div class="cal-meta">${esc(cls.lead)}</div>` : "") +
              (cls.location ? `<div class="cal-meta">${esc(cls.location)}</div>` : "") + `</td>`;
          } else {
            rows += `<td class="cal-noexp">—</td>`;
          }
        });
        rows += `</tr>`;
      });
    }

    const content = `<h2 style="margin:0 0 2px;color:#00305E;">${esc(studentName)} — Weekly Schedule</h2>
      <div style="color:#475569;font-size:12px;">Week of ${esc(rangeLabel)}</div>
      <table class="cal-table"><thead>${head}</thead><tbody>${rows}</tbody></table>
      <div class="legend">Off-site / internship days are highlighted in amber.</div>`;
    printPage({ title: `${studentName} — Weekly Schedule`, subtitle: "SMARTIS", content, landscape: true, extraStyles: CAL_STYLES });
  };

  const printMonthly = ({ activePlans }) => {
    const ref = new Date(refDate + "T00:00:00");
    const year = ref.getFullYear();
    const month = ref.getMonth();
    const monthLabel = ref.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    const first = new Date(year, month, 1);
    const gridStart = mondayOf(first);

    let body = "";
    let cur = new Date(gridStart);
    for (let w = 0; w < 6; w++) {
      // Only Mon–Fri columns
      let anyInMonth = false;
      let rowCells = "";
      for (let i = 0; i < 5; i++) {
        const dIso = iso(cur);
        const inMonth = cur.getMonth() === month;
        if (inMonth) anyInMonth = true;
        const t = dayTimes(weekdayKeyOf(dIso));
        const off = offsiteFor(activePlans, dIso);
        if (inMonth) {
          rowCells += `<td><div class="mon-date">${cur.getDate()}</div>` +
            (t.expected ? (t.label ? `<div class="mon-times">${esc(t.label)}</div>` : "") : `<div class="cal-noexp">Not expected</div>`) +
            (off ? `<div class="mon-off">Off-site: ${esc(off)}</div>` : "") + `</td>`;
        } else {
          rowCells += `<td class="mon-other"><div class="mon-date">${cur.getDate()}</div></td>`;
        }
        cur.setDate(cur.getDate() + 1);
      }
      // skip weekend
      cur.setDate(cur.getDate() + 2);
      if (anyInMonth || w === 0) body += `<tr>${rowCells}</tr>`;
      if (cur.getMonth() !== month && cur > new Date(year, month + 1, 0)) break;
    }

    const head = `<tr>${["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((d) => `<th>${d}</th>`).join("")}</tr>`;
    const content = `<h2 style="margin:0 0 2px;color:#00305E;">${esc(studentName)} — Monthly Schedule</h2>
      <div style="color:#475569;font-size:12px;">${esc(monthLabel)}</div>
      <table class="mon-grid"><thead>${head}</thead><tbody>${body}</tbody></table>
      <div class="legend">Amber = off-site / internship day. Times shown are the student's expected arrival–dismissal.</div>`;
    printPage({ title: `${studentName} — Monthly Schedule`, subtitle: "SMARTIS", content, landscape: true, extraStyles: CAL_STYLES });
  };

  const handlePrint = async () => {
    setBusy(true);
    try {
      const data = await loadData();
      if (view === "weekly") await printWeekly(data);
      else printMonthly(data);
      setOpen(false);
    } catch {
      toast.error("Could not build the calendar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="print-schedule-calendar-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          Print Calendar
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Print Schedule Calendar</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>View</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setView("weekly")} className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "weekly" ? "bg-[#00305E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="cal-view-weekly">Weekly</button>
              <button type="button" onClick={() => setView("monthly")} className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "monthly" ? "bg-[#00305E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="cal-view-monthly">Monthly</button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{view === "weekly" ? "Any date in the week" : "Any date in the month"}</Label>
            <Input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} data-testid="cal-ref-date" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handlePrint} disabled={busy} data-testid="cal-print-btn">{busy ? "Preparing…" : "Print"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
